import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

const MAX_RENDER_EDGE = 2000;

let workerInit: Promise<void> | null = null;

/**
 * pdf.js worker is emitted as .mjs; some nginx setups serve it as
 * application/octet-stream, which breaks module workers. Re-wrap as a
 * JavaScript Blob URL so conversion works regardless of MIME.
 */
function ensurePdfWorker(): Promise<void> {
  if (GlobalWorkerOptions.workerSrc?.startsWith('blob:')) {
    return Promise.resolve();
  }
  if (!workerInit) {
    workerInit = (async () => {
      try {
        const res = await fetch(pdfWorkerUrl);
        if (!res.ok) throw new Error(`worker HTTP ${res.status}`);
        const code = await res.text();
        const blob = new Blob([code], { type: 'text/javascript' });
        GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
      } catch {
        // Fallback: hope the static asset MIME is correct
        GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      }
    })();
  }
  return workerInit;
}

export type PdfToImagesOptions = {
  /** Max pages to convert (e.g. remaining attachment slots). */
  maxPages: number;
  /** Base name for generated files (without extension). */
  baseName?: string;
};

export type PdfToImagesResult = {
  files: File[];
  totalPages: number;
};

/**
 * Render PDF pages to JPEG File objects (client-side via pdf.js).
 * Does not upload; caller runs crop/redact then Storage upload.
 */
export async function pdfFileToJpegFiles(
  file: File,
  options: PdfToImagesOptions
): Promise<PdfToImagesResult> {
  const maxPages = Math.max(0, Math.floor(options.maxPages));
  if (maxPages <= 0) return { files: [], totalPages: 0 };

  await ensurePdfWorker();

  // Copy bytes — getDocument may transfer/detach the buffer
  const data = new Uint8Array(await file.arrayBuffer());
  const loadingTask = getDocument({ data: data.slice() });
  const pdf = await loadingTask.promise;
  const totalPages = pdf.numPages;
  const pageCount = Math.min(totalPages, maxPages);
  if (pageCount <= 0) {
    throw new Error('PDF_EMPTY');
  }

  const base =
    (options.baseName || file.name.replace(/\.pdf$/i, '') || 'pdf').replace(/[^\w.-]+/g, '_').slice(0, 40) ||
    'pdf';

  const out: File[] = [];
  for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const unscaled = page.getViewport({ scale: 1 });
    const scale = Math.min(2, MAX_RENDER_EDGE / Math.max(unscaled.width, unscaled.height, 1));
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('PDF_CANVAS');

    // White background (PDFs can be transparent)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('PDF_BLOB'))),
        'image/jpeg',
        0.92
      );
    });

    out.push(
      new File([blob], `${base}-p${pageNum}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      })
    );
  }

  return { files: out, totalPages };
}

export function isPdfFile(file: File): boolean {
  return (
    file.type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf')
  );
}
